import { DateOnly } from '@if/core/date'
import { EApplicantType, EFinancingProgram } from '@if/core/types'
import { Alert, Box, CircularProgress } from '@mui/material'
import { Breadcrumb } from '@src/components'
import BanishmentFormDialog from '@src/components/BanishmentFormDialog'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useCreateBanishment } from '@src/data/api/banisment/banishment'
import { useFraudFlags } from '@src/data/api/fraud/useFraud'
import { useIpqsDetails } from '@src/data/api/fraud/useIpqsDetails'
import { BanishmentForm } from '@src/data/types/BanSchema'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import FraudReportDialog from '../ViewCreditApplicationPage/components/FraudReportDialog'
import FraudFlagsContent from './components/FraudFlagsContent'
import { FraudFlagsHeader } from './components/FraudFlagsHeader'
import { FraudScoresPanel } from './components/FraudScoresPanel'

interface TabPanelProps {
  children?: React.ReactNode
  index: number | string
  value: number | string
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`fraud-flags-tabpanel-${index}`}
      aria-labelledby={`fraud-flags-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export const FraudFlagsPage: React.FC = () => {
  const { t } = useTranslation()
  const { id: creditApplicationId, financingProgramId } = useParams<{ id: string; financingProgramId: EFinancingProgram }>()

  const [tabIndicatorTypeValue, setTabIndicatorTypeValue] = useState(EApplicantType.Applicant)

  const { creditApplication, fraudAnalysis } = useCreditApplicationData()

  const [fraudFlagsData, isLoading, error] = useFraudFlags(financingProgramId || '', creditApplicationId || '')

  const [fraudReportDialogOpen, setFraudReportDialogOpen] = useState(false)
  const [banFormOpen, setBanFormOpen] = useState(false)
  const [createBanishment] = useCreateBanishment()

  const ipqsAnalysis = useMemo(() => {
    return tabIndicatorTypeValue === EApplicantType.CoApplicant
      ? fraudAnalysis?.coapplicantIPQSAnalysis
      : fraudAnalysis?.applicantIPQSAnalysis
  }, [fraudAnalysis, tabIndicatorTypeValue])

  const { emailDetail, phoneDetail, loading: ipqsDetailsLoading } = useIpqsDetails(ipqsAnalysis, creditApplicationId || '')

  const activeFlags = useMemo(() => {
    return Object.fromEntries(
      Object.entries(fraudFlagsData?.flags || {})
        .map(([indicatorType, flags]) => [
          indicatorType,
          flags.filter((f) => f.value)
        ])
    )
  }, [fraudFlagsData])
  const handleTabApplicantChange = (_event: React.SyntheticEvent, newValue: EApplicantType) => {
    setTabIndicatorTypeValue(newValue)

  }

  const handleOpenFraudReport = () => {
    setFraudReportDialogOpen(true)
  }

  const handleCloseFraudReport = () => {
    setFraudReportDialogOpen(false)
  }

  const handleOpenBanForm = useCallback(() => {
    setBanFormOpen(true)
  }, [])

  const handleBanFormConfirmed = useCallback(async (formData: BanishmentForm) => {
    try {
      await createBanishment({
        reason: formData.reason,
        expirationDate: formData.expirationDate,
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: formData.birthDate,
        sin: formData.sin,
      })
      setBanFormOpen(false)
    } catch (error) {
      console.error('Failed to create banishment:', error)
    }
  }, [createBanishment])

  const handleBanFormCancelled = useCallback(() => {
    setBanFormOpen(false)
  }, [])

  const banFormDefaultValues = useMemo(() => {
    if (!creditApplication) return {}
    const source = tabIndicatorTypeValue === EApplicantType.CoApplicant
      ? creditApplication.coApplicant
      : creditApplication.applicant
    if (!source) return {}
    return {
      firstName: source.firstName,
      lastName: source.lastName,
      birthDate: new DateOnly(source.birthDate),
      sin: source.sin || '',
      reason: '',
      expirationDate: null,
    }
  }, [tabIndicatorTypeValue, creditApplication])

  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
    {
      path: `/Applications/${financingProgramId}/${creditApplicationId}/view`,
      label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber || creditApplicationId}`),
    },
    { path: '#', label: t('fraudFlags.title') },
  ]

  if (!creditApplicationId || !financingProgramId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('fraudFlags.errors.noCreditApplicationId')}</Alert>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error.message || 'An error occurred while loading fraud flags'}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumb trees={breadCrumbs} />

      <FraudFlagsHeader
        creditApplicationId={creditApplicationId || ''}
        financingProgramId={fraudFlagsData?.financingProgramId || ''}
        totalFlags={fraudFlagsData?.flags[tabIndicatorTypeValue]?.length || 0}
        activeFlags={activeFlags[tabIndicatorTypeValue]?.length || 0}
        fraudAnalysis={fraudAnalysis}
        tabApplicantValue={tabIndicatorTypeValue || EApplicantType.Applicant}
        coApplicantDisabled={!(EApplicantType.CoApplicant in (fraudFlagsData?.flags || {}))}
        handleOpenFraudReport={handleOpenFraudReport}
        handleTabApplicantChange={handleTabApplicantChange}
      />
      <TabPanel value={tabIndicatorTypeValue} index={EApplicantType.Applicant}>
        <FraudScoresPanel
          fraudAnalysisResult={fraudAnalysis}
          ipqsAnalysis={fraudAnalysis?.applicantIPQSAnalysis}
          financingProgramId={financingProgramId}
          creditApplicationId={creditApplicationId || ''}
          onBanApplicant={handleOpenBanForm}
          banLabel={t('fraudFlags.fraudIQ.banApplicant')}
          emailDetail={emailDetail}
          phoneDetail={phoneDetail}
          ipqsDetailsLoading={ipqsDetailsLoading}
        />
        <FraudFlagsContent
          fraudFlags={fraudFlagsData?.flags[EApplicantType.Applicant]}
          indicatorType={EApplicantType.Applicant}
          financingProgramId={financingProgramId}
          creditApplicationId={creditApplicationId || ''}
          activeFlags={activeFlags[EApplicantType.Applicant] || []}
        />
      </TabPanel>
      <TabPanel value={tabIndicatorTypeValue} index={EApplicantType.CoApplicant}>
        <FraudScoresPanel
          fraudAnalysisResult={fraudAnalysis}
          ipqsAnalysis={fraudAnalysis?.coapplicantIPQSAnalysis}
          financingProgramId={financingProgramId}
          creditApplicationId={creditApplicationId || ''}
          onBanApplicant={handleOpenBanForm}
          banLabel={t('fraudFlags.fraudIQ.banCoApplicant')}
          emailDetail={emailDetail}
          phoneDetail={phoneDetail}
          ipqsDetailsLoading={ipqsDetailsLoading}
        />
        <FraudFlagsContent
          fraudFlags={fraudFlagsData?.flags[EApplicantType.CoApplicant]}
          indicatorType={EApplicantType.CoApplicant}
          financingProgramId={financingProgramId}
          creditApplicationId={creditApplicationId || ''}
          activeFlags={activeFlags[EApplicantType.CoApplicant] || []}
        />
      </TabPanel>

      {fraudAnalysis && (tabIndicatorTypeValue === EApplicantType.Applicant || tabIndicatorTypeValue === EApplicantType.CoApplicant) && (
        <FraudReportDialog
          open={fraudReportDialogOpen}
          onClose={handleCloseFraudReport}
          applicantType={tabIndicatorTypeValue}
          emailDetail={emailDetail}
          phoneDetail={phoneDetail}
          loading={ipqsDetailsLoading}
        />
      )}

      {banFormOpen && (
        <BanishmentFormDialog
          open
          onConfirm={handleBanFormConfirmed}
          onCancel={handleBanFormCancelled}
          defaultValues={banFormDefaultValues}
          readonly={{
            firstName: true,
            lastName: true,
            birthDate: true,
            sin: true,
          }}
        />
      )}
    </Box>
  )
}
